import { useMemo, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Search, SlidersHorizontal } from "lucide-react";

export default function ShopPage() {
    const [params] = useSearchParams();
    const [q, setQ] = useState(params.get("q") || "");
    const [filters, setFilters] = useState({ categoria: params.get("categoria") || "", genero: params.get("genero") || "", oferta: params.get("oferta") === "1", available: false, marca: "" });
    const [sort, setSort] = useState("recientes");

    useEffect(() => {
        setFilters((f) => ({ ...f, categoria: params.get("categoria") || "", genero: params.get("genero") || "", oferta: params.get("oferta") === "1" }));
        if (params.get("q")) setQ(params.get("q"));
    }, [params]);

    const { data: all = [] } = useQuery({ queryKey: ["products"], queryFn: async () => (await api.get("/products")).data });

    const brands = useMemo(() => Array.from(new Set(all.map((p) => p.brand).filter(Boolean))), [all]);

    const filtered = useMemo(() => {
        let list = all.filter((p) => {
            if (filters.categoria && p.category !== filters.categoria) return false;
            if (filters.genero && p.gender !== filters.genero) return false;
            if (filters.marca && p.brand !== filters.marca) return false;
            if (filters.oferta && !p.onSale) return false;
            if (filters.available && p.stock <= 0) return false;
            if (q.trim() && !(`${p.name} ${p.brand} ${p.category}`.toLowerCase().includes(q.trim().toLowerCase()))) return false;
            return true;
        });
        switch (sort) {
            case "precio-asc": list = [...list].sort((a, b) => a.price - b.price); break;
            case "precio-desc": list = [...list].sort((a, b) => b.price - a.price); break;
            case "destacados": list = [...list].sort((a, b) => Number(b.featured) - Number(a.featured)); break;
            default: list = [...list].sort((a, b) => Number(b.isNew) - Number(a.isNew));
        }
        return list;
    }, [all, filters, q, sort]);

    return (
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 py-10">
            <div className="mb-8">
                <p className="text-eyebrow">Colección</p>
                <h1 className="font-display text-4xl sm:text-5xl text-ink mt-2">Tienda</h1>
                <p className="text-sm text-ink/60 mt-2">{filtered.length} productos</p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 mb-8">
                <div className="relative flex-1">
                    <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-ink/40" />
                    <input data-testid="shop-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="¿Qué estás buscando?" className="w-full border border-sandLight bg-white rounded-full pl-10 pr-4 py-2.5 text-sm outline-none focus:border-ink" />
                </div>
                <select value={sort} onChange={(e) => setSort(e.target.value)} className="border border-sandLight bg-white rounded-full px-4 py-2.5 text-sm outline-none focus:border-ink sm:w-56">
                    <option value="recientes">Más recientes</option>
                    <option value="precio-asc">Precio: menor a mayor</option>
                    <option value="precio-desc">Precio: mayor a menor</option>
                    <option value="destacados">Destacados</option>
                </select>
            </div>

            <div className="flex flex-col lg:flex-row gap-10">
                <aside className="lg:w-64 shrink-0">
                    <div className="flex items-center gap-2 mb-4"><SlidersHorizontal className="w-4 h-4" /><p className="text-eyebrow">Filtros</p></div>
                    <div className="space-y-6">
                        <div>
                            <p className="text-xs uppercase tracking-wider text-ink/50 mb-2">Género</p>
                            {["", "Hombre", "Mujer", "Unisex"].map((g) => (
                                <label key={g || "all"} className="flex items-center gap-2 text-sm text-ink/80 py-1 cursor-pointer">
                                    <input type="radio" name="genero" checked={filters.genero === g} onChange={() => setFilters({ ...filters, genero: g })} className="accent-ink" />{g || "Todos"}
                                </label>
                            ))}
                        </div>
                        <div>
                            <p className="text-xs uppercase tracking-wider text-ink/50 mb-2">Marca</p>
                            <select value={filters.marca} onChange={(e) => setFilters({ ...filters, marca: e.target.value })} className="w-full border border-sandLight bg-white rounded-md px-3 py-2 text-sm">
                                <option value="">Todas</option>
                                {brands.map((b) => <option key={b} value={b}>{b}</option>)}
                            </select>
                        </div>
                        <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={filters.oferta} onChange={(e) => setFilters({ ...filters, oferta: e.target.checked })} className="accent-terracotta" />Solo ofertas</label>
                        <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={filters.available} onChange={(e) => setFilters({ ...filters, available: e.target.checked })} className="accent-ink" />Solo disponibles</label>
                    </div>
                </aside>
                <div className="flex-1">
                    {filtered.length === 0 ? (
                        <p className="text-sm text-ink/60 py-16 text-center">No encontramos productos con esos filtros.</p>
                    ) : (
                        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-x-4 gap-y-10">{filtered.map((p) => <ProductCard key={p.id} product={p} />)}</div>
                    )}
                </div>
            </div>
        </div>
    );
}
